// DateRangePicker component - single trigger button with range calendar popover
// CREATED: Purpose-built range date selector for analytics filter bar
// Features: Month/year dropdowns, range highlighting, Apply/Clear footer,
// smart positioning via portal, swipe support, French locale, no new dependencies
// UPDATED: Trigger button aligned with Team page compact filter button style
//   (px-4 py-2.5 min-h-[46px], font-medium, larger icons, ChevronDown indicator)

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { getPortalRoot } from '../../lib/portalUtils';

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  className?: string;
}

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function formatShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateStr(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function DateRangePicker({
  dateFrom,
  dateTo,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Sélectionner une période',
  className = '',
}: DateRangePickerProps) {
  const todayDate = new Date();
  const todayStr = toDateStr(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());

  const [isOpen, setIsOpen] = useState(false);
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo] = useState('');
  const [viewYear, setViewYear] = useState(todayDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayDate.getMonth());
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelHeight = 420;
    const panelWidth = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= panelHeight || spaceBelow > rect.top
      ? rect.bottom + 8
      : rect.top - panelHeight - 8;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
    setDropPos({ top, left });
  }, []);

  const openPicker = () => {
    setPendingFrom(dateFrom || '');
    setPendingTo(dateTo || '');
    if (dateFrom) {
      const d = new Date(dateFrom + 'T00:00:00');
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    } else {
      setViewYear(todayDate.getFullYear());
      setViewMonth(todayDate.getMonth());
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (isOpen) computePosition();
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const handleDayClick = (ds: string) => {
    if (minDate && ds < minDate) return;
    if (maxDate && ds > maxDate) return;
    if (!pendingFrom || (pendingFrom && pendingTo)) {
      setPendingFrom(ds);
      setPendingTo('');
    } else if (ds < pendingFrom) {
      setPendingFrom(ds);
      setPendingTo('');
    } else {
      setPendingTo(ds);
    }
  };

  const handleApply = () => {
    if (pendingFrom) onChange(pendingFrom, pendingTo || pendingFrom);
    setIsOpen(false);
  };

  const handleClearPending = () => {
    setPendingFrom('');
    setPendingTo('');
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const onTouchStart = (e: React.TouchEvent) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const d = touchStart - touchEnd;
    if (Math.abs(d) > 50) d > 0 ? nextMonth() : prevMonth();
  };

  const renderDay = (day: number, ds: string, outside: boolean) => {
    const disabled = !!(minDate && ds < minDate) || !!(maxDate && ds > maxDate);
    const isStart = ds === pendingFrom;
    const isEnd = ds === pendingTo;
    const isSelected = isStart || isEnd;
    const inRange = !!(pendingFrom && pendingTo && ds > pendingFrom && ds < pendingTo);
    const isToday = ds === todayStr;

    let cls = 'flex items-center justify-center text-sm font-medium transition-all min-h-[36px] min-w-[36px] rounded-lg ';
    if (disabled) {
      cls += 'text-gray-300 cursor-not-allowed';
    } else if (isSelected) {
      cls += 'bg-primary-500 text-white font-semibold shadow-sm';
    } else if (inRange) {
      cls += 'bg-primary-100 text-primary-800 rounded-none';
    } else if (outside) {
      cls += 'text-gray-300 hover:bg-gray-100 hover:text-gray-500 cursor-pointer';
    } else if (isToday) {
      cls += 'bg-primary-50 text-primary-700 border border-primary-300 hover:bg-primary-100 cursor-pointer';
    } else {
      cls += 'text-gray-700 hover:bg-gray-100 cursor-pointer';
    }

    return (
      <button key={ds} type="button" disabled={disabled} onClick={() => handleDayClick(ds)} className={cls}>
        {day}
      </button>
    );
  };

  const generateCalendarDays = () => {
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const firstDayRaw = new Date(viewYear, viewMonth, 1).getDay();
    const firstDay = firstDayRaw === 0 ? 6 : firstDayRaw - 1;
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
    const cells: React.ReactNode[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
      const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
      cells.push(renderDay(d, toDateStr(prevY, prevM, d), true));
    }

    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(renderDay(d, toDateStr(viewYear, viewMonth, d), false));
    }

    const weeksNeeded = Math.ceil((firstDay + daysInMonth) / 7);
    const remaining = weeksNeeded * 7 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
      cells.push(renderDay(d, toDateStr(nextY, nextM, d), true));
    }

    return cells;
  };

  const hasValue = !!(dateFrom || dateTo);
  const displayLabel = dateFrom && dateTo
    ? `${formatShort(dateFrom)}  —  ${formatShort(dateTo)}`
    : dateFrom
    ? formatShort(dateFrom)
    : placeholder;

  const currentYear = todayDate.getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 8 + i);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : openPicker()}
        className={`flex items-center gap-2 px-4 py-2.5 min-h-[46px] border rounded-lg text-sm font-medium transition-all bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          hasValue ? 'border-gray-300 text-gray-900 hover:bg-gray-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
        }`}
      >
        <Calendar size={16} className={hasValue ? 'text-gray-600 shrink-0' : 'text-gray-400 shrink-0'} />
        <span className="whitespace-nowrap">{displayLabel}</span>
        <ChevronDown size={16} className={`transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''} ${hasValue ? 'text-gray-600' : 'text-gray-400'}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          className="fixed bg-white border border-gray-200 rounded-xl shadow-xl w-80"
          style={{ top: dropPos.top, left: dropPos.left, zIndex: 9999 }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3">
            <select
              value={viewMonth}
              onChange={e => setViewMonth(Number(e.target.value))}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-700"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>
            <select
              value={viewYear}
              onChange={e => setViewYear(Number(e.target.value))}
              className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-700"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={prevMonth} className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all">
                <ChevronLeft size={15} />
              </button>
              <button type="button" onClick={nextMonth} className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all">
                <ChevronRight size={15} />
              </button>
            </div>
          </div>

          <div className="px-4 pb-3">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAY_NAMES.map(name => (
                <div key={name} className="text-center text-xs font-semibold text-gray-400 py-1">{name}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays()}
            </div>
          </div>

          <div className="flex items-center justify-between px-4 pt-2 pb-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClearPending}
              disabled={!pendingFrom && !pendingTo}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Effacer
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!pendingFrom}
              className="px-4 py-1.5 text-sm bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Appliquer
            </button>
          </div>
        </div>,
        getPortalRoot()
      )}
    </div>
  );
}
